import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import ControlledAutocomplete from 'src/components/ControlledFields/ControlledAutocomplete';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { PulseIconButton } from 'src/components/PulseUI/PulseIconButton';
import { useSearchHubSpotCompaniesQuery } from 'src/api/redux';
import {
  HUBSPOT_MIN_SEARCH_LENGTH,
  HUBSPOT_SEARCH_DEBOUNCE_MS,
} from './constants';

interface HubSpotCompanyFieldProps {
  /** The dialog is always mounted, so searching waits until it's open. */
  isOpen: boolean;
  isEdit: boolean;
  /**
   * The company can't be saved unlinked (MKT-869): drops "Connect to HubSpot
   * later" and makes the link a required field.
   */
  linkRequired?: boolean;
  /**
   * Whether a link saved before the dialog opened can be removed. The API
   * rejects unlinking a customer (MKT-869); relinking to another record still
   * works. A link picked in this session can always be removed.
   */
  canUnlinkSaved?: boolean;
}

/**
 * Several HubSpot records can share a name, so the domain is what tells them
 * apart — it's shown on every option.
 */
const formatHubSpotLabel = (name?: string | null, domain?: string | null) =>
  [name, domain].filter(Boolean).join(' — ');

/**
 * Links a company to its HubSpot record. The search box only searches; the
 * link itself is the card below it, so editing the search text can never
 * change or remove a link by accident.
 */
const HubSpotCompanyField = ({
  isOpen,
  isEdit,
  linkRequired = false,
  canUnlinkSaved = true,
}: HubSpotCompanyFieldProps) => {
  const { watch, setValue, clearErrors, setFocus, resetField, formState } =
    useFormContext();
  const [typedSearch, setTypedSearch] = useState('');

  const companyName: string = watch('name') ?? '';
  const linkedId: string | null = watch('hubspot_company_id');
  const linkedName: string | null = watch('hubspot_company_name');
  const linkedDomain: string | null = watch('hubspot_domain');
  // Linked when the dialog opened, as opposed to linked by a pick just now.
  const savedId: string | null =
    formState?.defaultValues?.hubspot_company_id ?? null;
  const wasLinked = !!savedId;
  const canDisconnect = canUnlinkSaved || !linkedId || linkedId !== savedId;
  // Removing a replacement pick on a company whose saved link must stay
  // restores that saved link instead of unlinking.
  const mustKeepSavedLink = !canUnlinkSaved && wasLinked;

  // Creating, the name comes *from* HubSpot, so there's nothing to seed with.
  // Linking an existing unlinked company later, its name is the best clue, so
  // the likely match is already waiting when the admin opens the picker.
  const seedSearch = isEdit && !linkedId ? companyName : '';
  const liveSearch = (typedSearch || seedSearch).trim();
  const [search] = useDebounce(liveSearch, HUBSPOT_SEARCH_DEBOUNCE_MS);
  // Gated on the live term too, so clearing the input clears the results
  // immediately rather than a debounce later.
  const isSearchable =
    liveSearch.length >= HUBSPOT_MIN_SEARCH_LENGTH &&
    search.length >= HUBSPOT_MIN_SEARCH_LENGTH;

  // currentData, not data: a skipped query keeps returning the last result.
  const { currentData: data, isFetching } = useSearchHubSpotCompaniesQuery(
    { search },
    { skip: !isOpen || !isSearchable }
  );

  const options = (isSearchable ? (data?.results ?? []) : []).map(
    (company) => ({
      value: company?.id,
      label: formatHubSpotLabel(company?.name, company?.domain),
      name: company?.name,
      domain: company?.domain,
    })
  );

  const emptyText = !isSearchable
    ? `Type at least ${HUBSPOT_MIN_SEARCH_LENGTH} characters to search HubSpot`
    : data?.hubspot_available === false
      ? "Couldn't reach HubSpot."
      : 'No HubSpot companies match';

  // The link is by id, so a different name is allowed — but it's worth a
  // second look, since it's usually a typo rather than a deliberate choice.
  const nameDiffersFromHubSpot =
    !!linkedId &&
    !!linkedName &&
    !!companyName.trim() &&
    companyName.trim() !== linkedName.trim();

  const handleOptionSelect = (option: { name?: unknown; domain?: unknown }) => {
    const name = typeof option?.name === 'string' ? option.name : null;
    const domain = typeof option?.domain === 'string' ? option.domain : null;
    // A pick always sets Name, replacing anything typed there — when creating,
    // and when linking a company that was saved before it could be linked
    // (e.g. HubSpot was down). Relinking a linked company never renames it;
    // "Use HubSpot name" does that on purpose.
    const shouldFillName = !!name && (!isEdit || !wasLinked);

    clearErrors('hubspot_company_id');
    // Dirty so re-picking the linked company can save a refreshed snapshot
    // (e.g. after a rename in HubSpot) even though the id hasn't changed.
    setValue('hubspot_company_name', name, { shouldDirty: true });
    setValue('hubspot_domain', domain, { shouldDirty: true });
    if (shouldFillName) {
      setValue('name', name, { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleDisconnect = () => {
    if (mustKeepSavedLink) {
      const saved = formState?.defaultValues;
      resetField('hubspot_company_id');
      // The snapshot fields are never registered, which resetField ignores.
      // shouldDirty re-checks them against their defaults, so they end clean.
      setValue('hubspot_company_name', saved?.hubspot_company_name ?? null, {
        shouldDirty: true,
      });
      setValue('hubspot_domain', saved?.hubspot_domain ?? null, {
        shouldDirty: true,
      });
      return;
    }
    clearErrors('hubspot_company_id');
    setValue('hubspot_company_id', null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('hubspot_company_name', null);
    setValue('hubspot_domain', null);
  };

  const handleUseHubSpotName = () => {
    setValue('name', linkedName, { shouldDirty: true, shouldValidate: true });
  };

  const handleLinkLater = () => {
    handleDisconnect();
    setFocus('name');
  };

  return (
    <div className="flex flex-col gap-1" data-testid="hubspot-company-field">
      <label
        className="text-sm font-semibold text-neutral-700 block"
        data-testid="hubspot-company-label"
      >
        HubSpot Company
      </label>
      <p
        className="text-xs text-neutral-500"
        data-testid="hubspot-company-helper"
      >
        {isEdit && !wasLinked
          ? "Link this company to its HubSpot record and the name will be updated from it. If the name is misspelled and no match appears, search part of the name or the company's website domain."
          : isEdit
            ? "Link this company to its HubSpot record. If the name is misspelled and no match appears, search part of the name or the company's website domain."
            : linkRequired
              ? 'Start by finding this company in HubSpot and the name will be filled in from it.'
              : 'Start by finding this company in HubSpot and the name will be filled in from it. Not in HubSpot yet? Choose "Connect to HubSpot later" and type the name below.'}
      </p>
      <ControlledAutocomplete
        name="hubspot_company_id"
        options={options}
        placeholder="Search HubSpot companies"
        loading={isFetching}
        onInputChange={setTypedSearch}
        onOptionSelect={handleOptionSelect}
        emptyText={emptyText}
        rules={
          linkRequired
            ? { required: 'Link this company to its HubSpot record' }
            : undefined
        }
        footerAction={
          linkRequired || mustKeepSavedLink
            ? undefined
            : { label: 'Connect to HubSpot later', onSelect: handleLinkLater }
        }
        searchOnly
      />
      <div className="pt-1" data-testid="hubspot-link-status">
        {linkedId ? (
          <div
            className="flex items-start justify-between gap-3 rounded-lg border border-sky-500 bg-sky-50 p-3"
            data-testid="hubspot-linked-company"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span
                className="truncate text-sm font-semibold text-neutral-800"
                data-testid="hubspot-linked-name"
              >
                {linkedName || 'HubSpot company'}
              </span>
              {linkedDomain && (
                <span
                  className="truncate text-sm text-neutral-700"
                  data-testid="hubspot-linked-domain"
                >
                  {linkedDomain}
                </span>
              )}
              {/* Labelled so it can't be mistaken for the MedScout company id. */}
              <span
                className="text-xs text-neutral-600"
                data-testid="hubspot-linked-id"
              >
                {`HubSpot ID: ${linkedId}`}
              </span>
            </div>
            {canDisconnect && (
              <PulseIconButton
                size="sm"
                variant="plain"
                color="neutral"
                icon={faXmark}
                onClick={handleDisconnect}
                aria-label={`Disconnect ${linkedName || 'HubSpot company'}`}
                data-testid="hubspot-linked-company-remove"
              />
            )}
          </div>
        ) : (
          <span
            className="text-sm text-neutral-500"
            data-testid="hubspot-not-connected"
          >
            Not connected to HubSpot
          </span>
        )}
      </div>
      {nameDiffersFromHubSpot && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3"
          role="status"
          data-testid="hubspot-name-differs-notice"
        >
          <Icon
            icon={faTriangleExclamation}
            className="shrink-0 pt-0.5 text-xs text-amber-600"
            aria-hidden="true"
          />
          <div className="flex flex-col items-start gap-2">
            <p
              className="text-sm text-amber-900"
              data-testid="hubspot-name-differs"
            >
              {`Saving with this name will differ from the HubSpot record ("${linkedName}"). Please make sure that's what you want.`}
            </p>
            <PulseButton
              variant="outline"
              color="neutral"
              size="sm"
              onClick={handleUseHubSpotName}
              data-testid="hubspot-use-name"
            >
              Use HubSpot name
            </PulseButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubSpotCompanyField;
